import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Input, NumericTextBox, Checkbox } from "@progress/kendo-react-inputs";
import { Button } from "@progress/kendo-react-buttons";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import { apiClient } from "src/dm/utils/axios";
import { DM_ROLES, useCurrentUser, useHasAnyRole } from "src/dm/auth";

interface UserPreferenceDto {
  userId: string;
  firstName: string;
  lastName: string;
  suppressTerms: boolean;
  suppressHelp: boolean;
  defaultVendorBrokerId: number | null;
  defaultCategoryManagerCode: number | null;
  acceptedTermsDate: string | null;
  showSecondaryOptions: boolean;
  enterVendorScanbackAmtPerPack: boolean;
  defaultOwnerUserId: string | null;
  paginateDefault: boolean;
}

interface UserPreferenceWriteRequest {
  suppressTerms: boolean;
  suppressHelp: boolean;
  defaultVendorBrokerId: number | null;
  defaultCategoryManagerCode: number | null;
  showSecondaryOptions: boolean;
  enterVendorScanbackAmtPerPack: boolean;
  defaultOwnerUserId: string | null;
  paginateDefault: boolean;
}

const PreferencesPage = () => {
  // userId picker — until auth lands we let the user type a real one. The
  // legacy app reads "GIANTEAGLE\\<short>" / "Peanuts\\<id>" style IDs.
  const [userId, setUserId] = useState("GiantEagle\\2065575");
  const [draft, setDraft] = useState<UserPreferenceWriteRequest | null>(null);
  const [tab, setTab] = useState(0);
  const qc = useQueryClient();

  // Role-gated section + tab visibility — mirrors legacy
  // ManageUserPreferences.aspx:
  //   * `divTerms` (the SPRS_TERMS_AND_CNDTN_FG checkbox + accepted-date
  //     label) was wrapped in `if (IsExternalUser)`. Internal users have a
  //     blanket terms-acceptance on login and don't see the row at all.
  //   * The Notifications sub-panel (btnSwitch toggles Notifications/Settings)
  //     only made sense for external creator/submitter roles — they're the
  //     ones who receive deal-state emails. Internal users don't get a
  //     personal notification opt-in panel.
  // Roles + flags come from `/api/dealmanagement/users/me` which is already
  // wired in `useCurrentUser`.
  const { data: currentUser } = useCurrentUser();
  const showTerms = currentUser?.isExternalUser ?? false;
  const showNotificationsTab = useHasAnyRole([
    DM_ROLES.ExternalCreator,
    DM_ROLES.ExternalSubmitter,
  ]);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["user-preference", userId],
    enabled: !!userId,
    queryFn: async (): Promise<UserPreferenceDto> => {
      const response = await apiClient.get<UserPreferenceDto>(
        `/dm/V1/user-preferences/${encodeURIComponent(userId)}`
      );
      return response.data;
    },
  });

  useEffect(() => {
    if (!data) {
      setDraft(null);
      return;
    }
    setDraft({
      suppressTerms: data.suppressTerms,
      suppressHelp: data.suppressHelp,
      defaultVendorBrokerId: data.defaultVendorBrokerId,
      defaultCategoryManagerCode: data.defaultCategoryManagerCode,
      showSecondaryOptions: data.showSecondaryOptions,
      enterVendorScanbackAmtPerPack: data.enterVendorScanbackAmtPerPack,
      defaultOwnerUserId: data.defaultOwnerUserId,
      paginateDefault: data.paginateDefault,
    });
  }, [data]);

  // Reset to the Settings tab when the visible-tab set changes (e.g. user
  // identity changes from external to internal between loads). Without this
  // a Daymon user could be stuck on a tab that no longer renders.
  useEffect(() => {
    if (!showNotificationsTab && tab > 0) setTab(0);
  }, [showNotificationsTab, tab]);

  const save = useMutation({
    mutationFn: async (body: UserPreferenceWriteRequest): Promise<UserPreferenceDto> => {
      const response = await apiClient.put<UserPreferenceDto>(
        `/dm/V1/user-preferences/${encodeURIComponent(userId)}`,
        body
      );
      return response.data;
    },
    onSuccess: (resp) => {
      qc.setQueryData(["user-preference", userId], resp);
    },
  });

  const setField = <K extends keyof UserPreferenceWriteRequest>(
    key: K,
    value: UserPreferenceWriteRequest[K]
  ) => setDraft((p) => (p ? { ...p, [key]: value } : p));

  // Pre-compute the Settings tab body so it can be shared between the
  // tabbed (external creator/submitter) and untabbed (everyone else)
  // render paths without duplicating JSX.
  const settingsBody = useMemo(() => {
    if (!data || !draft) return null;
    return (
      <>
        <div style={{ marginBottom: "0.75rem" }}>
          <strong>
            {data.firstName} {data.lastName}
          </strong>
          <div style={{ color: "#64748b", fontSize: "0.8rem" }}>{data.userId}</div>
          {/* Terms section — legacy `divTerms` wrapped in `IsExternalUser`.
              Internal users don't see this row at all; their terms are
              accepted implicitly via the corporate SSO sign-on. */}
          {showTerms && (
            <div
              style={{
                marginTop: "0.75rem",
                padding: "0.5rem 0.75rem",
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: "6px",
              }}
            >
              <div
                style={{
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  color: "#0f172a",
                  marginBottom: "0.25rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                Terms &amp; Conditions
              </div>
              {data.acceptedTermsDate ? (
                <div style={{ color: "#64748b", fontSize: "0.8rem" }}>
                  Accepted on {new Date(data.acceptedTermsDate).toLocaleString()}
                </div>
              ) : (
                <div style={{ color: "#b91c1c", fontSize: "0.8rem" }}>
                  Terms not yet accepted.
                </div>
              )}
              <div style={{ marginTop: "0.4rem" }}>
                <Checkbox
                  label="Suppress Terms &amp; Conditions prompt on next login"
                  checked={draft.suppressTerms}
                  onChange={(e) => setField("suppressTerms", !!e.value)}
                />
              </div>
            </div>
          )}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
          <label>
            <span style={{ fontSize: "0.8rem", color: "#475569" }}>
              Default Vendor / Broker ID
            </span>
            <NumericTextBox
              value={draft.defaultVendorBrokerId ?? null}
              onChange={(e) => setField("defaultVendorBrokerId", e.value ?? null)}
            />
          </label>
          <label>
            <span style={{ fontSize: "0.8rem", color: "#475569" }}>
              Default Category Manager Code
            </span>
            <NumericTextBox
              value={draft.defaultCategoryManagerCode ?? null}
              onChange={(e) => setField("defaultCategoryManagerCode", e.value ?? null)}
            />
          </label>
          <label style={{ gridColumn: "1 / -1" }}>
            <span style={{ fontSize: "0.8rem", color: "#475569" }}>
              Default Owner User ID
            </span>
            <Input
              value={draft.defaultOwnerUserId ?? ""}
              onChange={(e) =>
                setField("defaultOwnerUserId", String(e.target.value ?? "") || null)
              }
            />
          </label>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "0.4rem 1rem",
            marginTop: "1rem",
          }}
        >
          {/* `suppressTerms` lives in the Terms section above for external
              users; render the toggle here only when the dedicated section
              isn't shown so internal users (who can still suppress the
              one-time help/welcome flow via the same flag) keep parity. */}
          {!showTerms && (
            <Checkbox
              label="Suppress Terms &amp; Conditions"
              checked={draft.suppressTerms}
              onChange={(e) => setField("suppressTerms", !!e.value)}
            />
          )}
          <Checkbox
            label="Suppress Help"
            checked={draft.suppressHelp}
            onChange={(e) => setField("suppressHelp", !!e.value)}
          />
          <Checkbox
            label="Show Secondary Promotion Options"
            checked={draft.showSecondaryOptions}
            onChange={(e) => setField("showSecondaryOptions", !!e.value)}
          />
          <Checkbox
            label="Enter Vendor Scanback as Amount-per-Pack"
            checked={draft.enterVendorScanbackAmtPerPack}
            onChange={(e) => setField("enterVendorScanbackAmtPerPack", !!e.value)}
          />
          <Checkbox
            label="Paginate Dashboard by Default"
            checked={draft.paginateDefault}
            onChange={(e) => setField("paginateDefault", !!e.value)}
          />
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "1rem",
          }}
        >
          <Button
            themeColor="primary"
            onClick={() => save.mutate(draft)}
            disabled={save.isPending}
          >
            {save.isPending ? "Saving…" : "Save preferences"}
          </Button>
        </div>
        {save.isError && (
          <pre style={{ color: "#b91c1c", fontSize: "0.8rem", marginTop: "0.5rem" }}>
            {save.error instanceof Error ? save.error.message : "Save failed"}
          </pre>
        )}
      </>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, draft, save.isPending, save.isError, save.error, showTerms]);

  return (
    <div style={{ display: "grid", gap: "1rem" }}>
      <h1 style={{ marginTop: 0 }}>User Preferences</h1>

      <section
        style={{
          background: "#fff",
          padding: "1rem 1.25rem",
          borderRadius: "8px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        }}
      >
        <label style={{ display: "block", fontSize: "0.8rem", color: "#475569" }}>
          User ID (until auth lands)
        </label>
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          <Input
            value={userId}
            onChange={(e) => setUserId(String(e.target.value ?? ""))}
            style={{ maxWidth: "320px" }}
          />
          <Button type="button" onClick={() => refetch()}>
            Load
          </Button>
        </div>
        {isLoading && <p>Loading…</p>}
        {isError && (
          <p style={{ color: "#b91c1c", marginTop: "0.5rem" }}>
            {error instanceof Error ? error.message : "User not found"}
          </p>
        )}
      </section>

      {data && draft && (
        <section
          style={{
            background: "#fff",
            padding: "1rem 1.25rem",
            borderRadius: "8px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          }}
        >
          {showNotificationsTab ? (
            // External creator/submitter sees the full tabbed layout.
            // Legacy used the `btnSwitch` toggle to flip between panels;
            // a tabstrip is closer to the modern UX and still keeps the
            // two sections distinct.
            <TabStrip selected={tab} onSelect={(e) => setTab(e.selected)}>
              <TabStripTab title="Settings">{settingsBody}</TabStripTab>
              <TabStripTab title="Notifications">
                <NotificationsPanel userId={userId} />
              </TabStripTab>
            </TabStrip>
          ) : (
            // Everyone else (internal users, external viewers, Daymon)
            // gets the settings panel directly without an empty-tab chrome.
            settingsBody
          )}
        </section>
      )}
    </div>
  );
};

/**
 * Notifications sub-panel — only rendered when the user holds
 * DM_EXTERNAL_CREATOR or DM_EXTERNAL_SUBMITTER (legacy
 * ManageUserPreferences `gvNotifyPref` + add panel). The API endpoints for
 * per-user notification preferences aren't wired yet on the SPA side
 * (audit/05-admin-and-prefs.md §1 marks the sub-panel as DROPPED), so for
 * now this surfaces a placeholder. The role gate itself is what the
 * legacy parity check requires; the table population is a follow-up.
 */
interface NotificationsPanelProps {
  userId: string;
}
const NotificationsPanel = ({ userId }: NotificationsPanelProps) => {
  return (
    <div style={{ padding: "0.5rem 0" }}>
      <p style={{ color: "#64748b", fontSize: "0.85rem", margin: 0 }}>
        Manage notification subscriptions for <strong>{userId}</strong>.
      </p>
      <p style={{ color: "#94a3b8", fontSize: "0.8rem", marginTop: "0.5rem" }}>
        Notification preferences will appear here. The legacy
        <code style={{ margin: "0 0.25rem" }}>gvNotifyPref</code> grid
        (description / frequency / delete) is being ported.
      </p>
    </div>
  );
};

export default PreferencesPage;
